import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  clearClientToken,
  getClientToken,
  setClientToken,
} from 'lib/auth/token';
import { invalidateQueryCache } from 'hooks/useQuery';

export interface InitialAuthState {
  isLoggedIn: boolean;
  token?: string;
}

interface AuthContextValue {
  isLoggedIn: boolean;
  token?: string;
  setLoggedIn: (token: string) => void;
  logout: () => void;
  refreshUser: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue>({
  isLoggedIn: false,
  token: undefined,
  setLoggedIn: () => {},
  logout: () => {},
  refreshUser: async () => undefined,
});

export const AuthProvider = ({
  children,
  initialAuth,
}: {
  children: React.ReactNode;
  initialAuth?: InitialAuthState;
}) => {
  const [token, setToken] = useState<string | undefined>(initialAuth?.token);
  const [isLoggedIn, setIsLoggedIn] = useState(
    Boolean(initialAuth?.isLoggedIn)
  );

  useEffect(() => {
    const nextToken = getClientToken();
    setToken(nextToken);
    setIsLoggedIn(Boolean(nextToken));
  }, []);

  const setLoggedIn = useCallback((token: string) => {
    setClientToken(token);
    setToken(token);
    setIsLoggedIn(true);
    invalidateQueryCache('users.me');
  }, []);

  const logout = useCallback(() => {
    clearClientToken();
    setToken(undefined);
    setIsLoggedIn(false);
    invalidateQueryCache('users.me');
  }, []);

  const refreshUser = useCallback(async () => {
    const nextToken = getClientToken();
    setToken(nextToken);
    setIsLoggedIn(Boolean(nextToken));
    return nextToken;
  }, []);

  const value = useMemo(
    () => ({ isLoggedIn, token, setLoggedIn, logout, refreshUser }),
    [isLoggedIn, token, setLoggedIn, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
